import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TenantDateField } from '../../../components/TenantDateField';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { DocumentData } from 'firebase/firestore';

interface EditTermDialogProps {
  open: boolean;
  onClose: () => void;
  term: DocumentData | null;
}

export function EditTermDialog({ open, onClose, term }: EditTermDialogProps) {
  const { t } = useTranslation();
  const { currentTenantId } = useTenantStore();
  const { updateTerm, isUpdatingTerm } = useAcademic();

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [enrollmentDeadline, setEnrollmentDeadline] = useState('');
  // Entrega 2: explicit enrollment window. Supersedes the legacy deadline when
  // both dates are set; both-or-neither (clearing both removes the window).
  const [windowOpensAt, setWindowOpensAt] = useState('');
  const [windowClosesAt, setWindowClosesAt] = useState('');
  const [status, setStatus] = useState<'planned' | 'open' | 'in_progress' | 'closed'>('planned');
  const [error, setError] = useState<string | null>(null);

  // Converts string | Firestore Timestamp | plain-object | Date → "YYYY-MM-DD" or ""
  const toDateStr = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val; // already YYYY-MM-DD
      const d = new Date(val);
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]!;
    }
    if (typeof val.toDate === 'function') {
      // Firestore Timestamp (client SDK)
      return val.toDate().toISOString().split('T')[0]!;
    }
    if (val instanceof Date) return val.toISOString().split('T')[0]!;
    // Plain object {seconds, _seconds} (serialized Timestamp)
    const secs = val.seconds ?? val._seconds;
    if (typeof secs === 'number') return new Date(secs * 1000).toISOString().split('T')[0]!;
    return '';
  };

  useEffect(() => {
    if (open && term) {
      setName(term.name || '');
      setStartDate(toDateStr(term.startDate));
      setEndDate(toDateStr(term.endDate));
      setEnrollmentDeadline(toDateStr(term.enrollmentDeadline));
      setWindowOpensAt(toDateStr(term.enrollmentWindow?.opensAt));
      setWindowClosesAt(toDateStr(term.enrollmentWindow?.closesAt));
      setStatus(term.status || 'planned');
      setError(null);
    }
  }, [open, term]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !term) return;

    setError(null);

    const opens = toDateStr(windowOpensAt);
    const closes = toDateStr(windowClosesAt);
    // Both-or-neither: a half-set window is ambiguous.
    if ((opens && !closes) || (!opens && closes)) {
      setError(t('sis:editTerm.errors.windowBoth'));
      return;
    }
    if (opens && closes && opens > closes) {
      setError(t('sis:editTerm.errors.windowOrder'));
      return;
    }

    try {
      const payload: any = {
        tenantId: currentTenantId,
        termId: term.id,
        name,
        status,
      };

      // Use toDateStr so we handle Timestamp objects that may still be in state
      const sd = toDateStr(startDate);
      const ed = toDateStr(endDate);
      const dl = toDateStr(enrollmentDeadline);

      if (sd) payload.startDate = sd;
      if (ed) payload.endDate = ed;
      if (dl) payload.enrollmentDeadline = dl;
      // Set the window when both dates are present, or null to clear it.
      payload.enrollmentWindow = opens && closes ? { opensAt: opens, closesAt: closes } : null;

      await updateTerm(payload);
      onClose();
    } catch (err: any) {
      setError(err.message || t('sis:editTerm.errors.updating'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('sis:editTerm.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label={t('sis:editTerm.name')}
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <TenantDateField
              label={t('sis:editTerm.startDate')}
              value={startDate}
              onChange={setStartDate}
              fullWidth
            />

            <TenantDateField
              label={t('sis:editTerm.endDate')}
              value={endDate}
              onChange={setEndDate}
              fullWidth
            />

            <TenantDateField
              label={t('sis:editTerm.window.opensAt')}
              value={windowOpensAt}
              onChange={setWindowOpensAt}
              fullWidth
            />

            <TenantDateField
              label={t('sis:editTerm.window.closesAt')}
              value={windowClosesAt}
              onChange={setWindowClosesAt}
              helperText={t('sis:editTerm.window.help')}
              fullWidth
            />

            <TenantDateField
              label={t('sis:editTerm.deadline')}
              value={enrollmentDeadline}
              onChange={setEnrollmentDeadline}
              helperText={t('sis:editTerm.deadlineHelp')}
              fullWidth
            />

            <TextField
              select
              label={t('sis:editTerm.status.label')}
              required
              fullWidth
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              <MenuItem value="planned">{t('sis:editTerm.status.planned')}</MenuItem>
              <MenuItem value="open">{t('sis:editTerm.status.open')}</MenuItem>
              <MenuItem value="in_progress">{t('sis:editTerm.status.inProgress')}</MenuItem>
              <MenuItem value="closed">{t('sis:editTerm.status.closed')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={isUpdatingTerm}>
            {t('sis:editTerm.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUpdatingTerm}
            sx={{ minWidth: 120 }}
          >
            {isUpdatingTerm ? t('sis:editTerm.saving') : t('sis:editTerm.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
